import Breadcrumb from "components/Common/Breadcrumb"
import { useEffect, useMemo, useState } from "react"
import {
  FaAlignLeft,
  FaBuilding,
  FaCalendarAlt,
  FaCheck,
  FaClipboardList,
  FaClock,
  FaCrown,
  FaEdit,
  FaEye,
  FaFileAlt,
  FaFileExcel,
  FaFlag,
  FaHistory,
  FaPaperclip,
  FaPlus,
  FaSave,
  FaSearch,
  FaSync,
  FaTasks,
  FaTimes,
  FaUsers,
} from "react-icons/fa"
import { MdCancel, MdDelete, MdNoteAdd } from "react-icons/md"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import React from "react"
import Swal from "sweetalert2"
import moment from "moment"
import Loader from "components/Loader/Loader"
import secureLocalStorage from "react-secure-storage"
import Configs from "../config"
import { usePermissionData, usePrioritiesData } from "AppContext"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import Select from "react-select"
import { Switch } from "antd"
import * as XLSX from "xlsx"

// Status colors are a UI concern (the Task Status table only stores the name)
const STATUS_COLORS = {
  "to do": "#6c757d",
  "in progress": "#0d6efd",
  "on hold": "#ffc107",
  completed: "#198754",
  cancelled: "#dc3545",
}
const STATUS_FALLBACK_COLORS = [
  "#6f42c1",
  "#20c997",
  "#fd7e14",
  "#d63384",
  "#0dcaf0",
  "#6610f2",
]
const CLOSED_STATUS_NAMES = ["completed", "cancelled"]
// Bootstrap badge colors for the shared priorities list (by position)
const PRIORITY_BADGE_COLORS = [
  "danger",
  "danger",
  "warning",
  "success",
  "info",
  "primary",
  "secondary",
  "dark",
]

const ADMIN_STAFF_ID = 1

const RECURRENCE_OPTIONS = ["Daily", "Weekly", "Monthly"]

const EMPTY_FORM = {
  title: "",
  description: "",
  statusId: null,
  assignees: [],
  departmentId: null,
  headId: null,
  startDate: moment().format("YYYY-MM-DD"),
  startTime: moment().format("HH:mm"),
  dueDate: "",
  dueTime: "",
  priorityId: null,
  recurring: false,
  recurrence: null,
  isStatutoryTask: false,
}

const getPriorityUnit = priority =>
  priority?.unit === "days" ? "days" : "hours"

// Combine a date (YYYY-MM-DD) and optional time (HH:mm) field into a moment
const toTaskMoment = (date, time) =>
  date
    ? time
      ? moment(`${date} ${time}`, "YYYY-MM-DD HH:mm")
      : moment(date, "YYYY-MM-DD")
    : null

// "DD-MM-YYYY | hh:mm A" when a time exists, otherwise date only (matches Priorities page)
const formatTaskDateTime = (date, time) => {
  if (!date) return "-"
  if (time) {
    const m = toTaskMoment(date, time)
    if (m?.isValid()) return m.format("DD-MM-YYYY | hh:mm A")
  }
  return moment(date, "YYYY-MM-DD").format("DD-MM-YYYY")
}

const getInitials = name =>
  (name || "-")
    .split(" ")
    .map(part => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()

const getNextOccurrenceText = (startDate, recurrence) => {
  if (!startDate || !recurrence) return null
  const date = moment(startDate, "YYYY-MM-DD")
  if (!date.isValid()) return null
  const today = moment().startOf("day")
  const next = date.clone().startOf("day")
  const stepOnce = () => {
    if (recurrence === "Daily") next.add(1, "day")
    else if (recurrence === "Weekly") next.add(1, "week")
    else {
      const day = next.date()
      next.date(1).add(1, "month")
      next.date(Math.min(day, next.daysInMonth()))
    }
  }
  // First run is one period after the start date, then roll forward to today
  stepOnce()
  let guard = 0
  while (next.isBefore(today) && guard < 500) {
    stepOnce()
    guard++
  }
  return next.format("ddd, DD MMM YYYY")
}

// Shared Add/Edit Task modal field styling (adapted from add-task-redesign.html)
const TASK_INPUT_STYLE = {
  height: 48,
  fontSize: 14,
  borderRadius: 11,
  borderColor: "#d8e1ef",
}
const TASK_FIELD_CARD_STYLE = {
  padding: 16,
  height: "100%",
  border: "1px solid #edf1f7",
  borderRadius: 15,
  background: "linear-gradient(145deg, #ffffff, #f8faff)",
}
const TASK_LABEL_ICON_STYLE = { size: 16, color: "#4b607f" }

// Solid backgrounds for the assignees dropdown (app CSS leaves it transparent)
const ASSIGNEE_SELECT_STYLES = {
  control: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
    borderColor: "#d8e1ef",
    borderRadius: 11,
    minHeight: 48,
  }),
  menu: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  menuList: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#cb9019"
      : state.isFocused
      ? "#faf3e2"
      : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#263a5b",
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: "#faf3e2",
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: "#6b5a33",
  }),
}

// Shared Task Log modal field styling (aligned with the task modal)
const LOG_INPUT_STYLE = {
  height: 48,
  fontSize: 14,
  borderRadius: 11,
  borderColor: "#d8e1ef",
}
const LOG_TEXTAREA_STYLE = {
  minHeight: 96,
  padding: "12px 15px",
  lineHeight: 1.5,
  fontSize: 14,
  borderRadius: 11,
  borderColor: "#d8e1ef",
}
const LOG_FIELD_CARD_STYLE = {
  padding: "16px 18px",
  border: "1px solid #edf1f7",
  borderRadius: 15,
  background: "linear-gradient(145deg, #ffffff, #f8faff)",
}
const LOG_LABEL_STYLE = { fontSize: 14, fontWeight: 700 }

const TaskManagement = () => {
  document.title = "Task Management | Vintage Infra Equipment Sol PVT LTD"
  const currentStaffId = secureLocalStorage.getItem("admin_user")?.id ?? null
  const { checkAccess } = usePermissionData()

  const [data, setData] = useState([])
  const [statuses, setStatuses] = useState([])
  const [employees, setEmployees] = useState([])
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(false)
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [priorityFilter, setPriorityFilter] = useState("")

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})

  // Task logs (loaded per task when its history / timeline is opened)
  const [taskLogs, setTaskLogs] = useState([])
  const [logModalOpen, setLogModalOpen] = useState(false)
  const [logForm, setLogForm] = useState({
    id: null,
    taskId: null,
    dateTime: new Date().toISOString(),
    statusId: null,
    description: "",
    submittedById: null,
    attachment: null,
    dependancy: false,
    dependancyStaff: [],
  })
  const [logErrors, setLogErrors] = useState({})
  // Log review — only the task head approves / rejects (reason needed to reject)
  const [rejectTarget, setRejectTarget] = useState(null)
  const [rejectReason, setRejectReason] = useState("")
  const [rejectError, setRejectError] = useState("")
  const [reasonLog, setReasonLog] = useState(null) // rejected log to inspect
  const [historyTaskId, setHistoryTaskId] = useState(null)
  const [timelineTaskId, setTimelineTaskId] = useState(null)

  const { priorities } = usePrioritiesData()

  useEffect(() => {
    getTasks()
    getStatuses()
    getEmployees()
    getDepartments()
  }, [])

  async function getTasks() {
    const APIData = await fetchData(setLoading, "tasks")
    if (APIData?.success) {
      setData(APIData?.data)
    }
  }

  async function getStatuses() {
    const APIData = await fetchData(() => {}, "task-status")
    if (APIData?.success) {
      setStatuses(APIData?.data)
    }
  }

  async function getEmployees() {
    const APIData = await fetchData(() => {}, "users")
    if (APIData?.success) {
      setEmployees(APIData?.data.filter(user => user.status))
    }
  }

  async function getDepartments() {
    const APIData = await fetchData(() => {}, "departments")
    if (APIData?.success) {
      setDepartments(APIData?.data)
    }
  }

  async function loadTaskLogs(taskId) {
    const APIData = await fetchData(setLoading, `tasks/${taskId}/logs`)
    if (APIData?.success) {
      setTaskLogs(prev => [
        ...prev.filter(l => l.taskId !== taskId),
        ...APIData.data,
      ])
    }
  }

  const openHistory = taskId => {
    setHistoryTaskId(taskId)
    loadTaskLogs(taskId)
  }

  const openTimeline = taskId => {
    setTimelineTaskId(taskId)
    loadTaskLogs(taskId)
  }

  const employeeOptions = useMemo(
    () => employees.map(emp => ({ value: emp.id, label: emp.name })),
    [employees]
  )

  // The users API leaves out the default admin account (id 1)
  const getEmployeeName = id =>
    employees.find(emp => emp.id === id)?.name ||
    (id === ADMIN_STAFF_ID ? "Admin" : "-")

  // Statuses
  const activeStatuses = statuses.filter(s => s.isActive)

  const getStatusName = id => statuses.find(s => s.id === id)?.statusName || "-"

  const getStatusColor = id => {
    const idx = statuses.findIndex(s => s.id === id)
    if (idx < 0) return "#6c757d"
    const known = STATUS_COLORS[statuses[idx].statusName?.toLowerCase()]
    return known || STATUS_FALLBACK_COLORS[idx % STATUS_FALLBACK_COLORS.length]
  }

  const isClosedStatus = id =>
    CLOSED_STATUS_NAMES.includes(getStatusName(id).toLowerCase())

  // New tasks start on "To Do" when it exists, otherwise the first active status
  const defaultStatusId =
    (
      activeStatuses.find(s => s.statusName?.toLowerCase() === "to do") ||
      activeStatuses[0]
    )?.id ?? null

  // Active statuses, plus the one already selected (it may have been deactivated)
  const statusOptionsFor = selectedId =>
    statuses.filter(s => s.isActive || s.id === selectedId)

  // Department head: single staff = that staff, otherwise the stored head
  const getDepartmentHeadId = departmentId => {
    const department = departments.find(d => d.id === departmentId)
    if (!department) return null
    const deptEmployees = department.employees || []
    if (deptEmployees.length === 1) return deptEmployees[0]
    if (deptEmployees.includes(department.headId)) return department.headId
    return deptEmployees[0] ?? null
  }

  // Priorities
  const getPriority = id => priorities.find(p => p.id === id) || null

  const getPriorityColor = id => {
    const idx = priorities.findIndex(p => p.id === id)
    return idx >= 0 ? PRIORITY_BADGE_COLORS[idx] || "secondary" : "secondary"
  }

  // Auto-fill start (now) and due (start + priority value) when a priority is picked
  const buildPriorityDates = priorityId => {
    const priority = getPriority(priorityId)
    if (!priority?.value) return {}
    const start = moment().startOf("minute")
    const due = start
      .clone()
      .add(Number(priority.value), getPriorityUnit(priority))
    return {
      startDate: start.format("YYYY-MM-DD"),
      startTime: start.format("HH:mm"),
      dueDate: due.format("YYYY-MM-DD"),
      dueTime: due.format("HH:mm"),
    }
  }

  const handlePriorityChange = e => {
    const priorityId = e.target.value ? Number(e.target.value) : null
    const priority = getPriority(priorityId)
    setSelectedTask(prev => {
      const next = { ...prev, priorityId }
      if (!priority?.value) return next
      // Keep the existing start; only recompute due from it
      const start = toTaskMoment(prev.startDate, prev.startTime)
      if (!start) return { ...next, ...buildPriorityDates(priorityId) }
      const due = start
        .clone()
        .add(Number(priority.value), getPriorityUnit(priority))
      return {
        ...next,
        dueDate: due.format("YYYY-MM-DD"),
        dueTime: due.format("HH:mm"),
      }
    })
  }
  const matchesSearch = (item, search) =>
    !search ||
    item?.title?.toLowerCase().includes(search) ||
    item?.description?.toLowerCase().includes(search) ||
    (!!item?.headId &&
      getEmployeeName(item.headId).toLowerCase().includes(search)) ||
    (!!item?.createdById &&
      getEmployeeName(item.createdById).toLowerCase().includes(search)) ||
    item?.assignees?.some(id =>
      getEmployeeName(id).toLowerCase().includes(search)
    )

  const filteredData = useMemo(() => {
    const search = searchString.toLowerCase()
    return data.filter(item => {
      const matchesStatus =
        !statusFilter || item.statusId === Number(statusFilter)
      const matchesPriority =
        !priorityFilter || item.priorityId === Number(priorityFilter)
      return matchesSearch(item, search) && matchesStatus && matchesPriority
    })
  }, [data, searchString, statusFilter, priorityFilter, employees])

  // Counts for the status pills (respect search + priority, ignore status)
  const statusCounts = useMemo(() => {
    const search = searchString.toLowerCase()
    const base = data.filter(
      item =>
        matchesSearch(item, search) &&
        (!priorityFilter || item.priorityId === Number(priorityFilter))
    )
    const counts = { "": base.length }
    statuses.forEach(s => {
      counts[s.id] = base.filter(t => t.statusId === s.id).length
    })
    return counts
  }, [data, searchString, priorityFilter, statuses, employees])

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1
  const start = (currentPage - 1) * pageSize
  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize, start])

  const resetPage = () => setCurrentPage(1)

  const hasActiveFilters = searchString || statusFilter || priorityFilter

  const clearFilters = () => {
    setSearchString("")
    setStatusFilter("")
    setPriorityFilter("")
    setCurrentPage(1)
  }

  const getDepartmentName = id =>
    departments.find(dept => dept.id === id)?.departmentName || "-"

  const exportToExcel = () => {
    const rows = filteredData.map((item, i) => ({
      "#": i + 1,
      Title: item.title || "",
      Description: item.description || "",
      Status: getStatusName(item.statusId),
      Assignees: (item.assignees || []).map(getEmployeeName).join(", "),
      "Task Head": item.headId ? getEmployeeName(item.headId) : "-",
      "Created By": getEmployeeName(item.createdById),
      Department: item.departmentId
        ? getDepartmentName(item.departmentId)
        : "-",
      "Start Date": item.startDate
        ? moment(item.startDate).format("DD-MM-YYYY")
        : "-",
      "Start Time": item.startTime || "-",
      "Due Date": item.dueDate
        ? moment(item.dueDate).format("DD-MM-YYYY")
        : "-",
      "Due Time": item.dueTime || "-",
      Priority: getPriority(item.priorityId)?.priorityName || "",
      Recurring: item.recurring ? item.recurrence || "Yes" : "No",
      "Created At": item.createdAt
        ? moment(item.createdAt).format("DD-MM-YYYY")
        : "-",
    }))

    const ws = XLSX.utils.json_to_sheet(rows)
    ws["!cols"] = [
      { wch: 5 }, // #
      { wch: 35 }, // Title
      { wch: 45 }, // Description
      { wch: 14 }, // Status
      { wch: 30 }, // Assignees
      { wch: 20 }, // Task Head
      { wch: 20 }, // Created By
      { wch: 20 }, // Department
      { wch: 13 }, // Start Date
      { wch: 13 }, // Start Time
      { wch: 13 }, // Due Date
      { wch: 13 }, // Due Time
      { wch: 11 }, // Priority
      { wch: 12 }, // Recurring
      { wch: 13 }, // Created At
    ]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Tasks")
    XLSX.writeFile(wb, `Tasks_${moment().format("DD-MM-YYYY")}.xlsx`)
  }

  const openAddModal = () => {
    const priorityId = priorities[0]?.id ?? null
    setSelectedTask({
      ...EMPTY_FORM,
      statusId: defaultStatusId,
      priorityId,
      ...buildPriorityDates(priorityId),
    })
    setErrors({})
    setModalOpen(true)
  }

  const openEditModal = item => {
    setSelectedTask({
      ...item,
      assignees: [...(item.assignees || [])],
      headId: item?.headId ?? getDepartmentHeadId(item?.departmentId),
    })
    setErrors({})
    setModalOpen(true)
  }

  const handleDepartmentChange = e => {
    const departmentId = e.target.value ? Number(e.target.value) : null
    const department = departments.find(d => d.id === departmentId)
    const departmentHeadId = getDepartmentHeadId(departmentId)
    setSelectedTask(prev => ({
      ...prev,
      departmentId,
      assignees: department
        ? [...(department.employees || [])]
        : prev.assignees,
      // Auto-fill the task head with the department head (can be changed after)
      headId: departmentHeadId ?? prev.headId,
    }))
  }
  const validate = () => {
    const newErrors = {}
    if (!selectedTask?.title?.trim()) {
      newErrors.title = "Title is required"
    }
    if (!selectedTask?.statusId) {
      newErrors.status = "Status is required"
    }
    if (!selectedTask?.headId) {
      newErrors.headId = "Task head is mandatory"
    }
    const startMoment = toTaskMoment(
      selectedTask?.startDate,
      selectedTask?.startTime
    )
    const dueMoment = toTaskMoment(selectedTask?.dueDate, selectedTask?.dueTime)
    if (!selectedTask?.startDate) {
      newErrors.startDate = "Start date is required"
    }
    if (!selectedTask?.startTime) {
      newErrors.startTime = "Start time is required"
    }
    if (!selectedTask?.dueDate) {
      newErrors.dueDate = "Due date is required"
    }
    if (!selectedTask?.dueTime) {
      newErrors.dueTime = "Due time is required"
    }
    if (startMoment && dueMoment && !dueMoment.isAfter(startMoment)) {
      newErrors.dueDate = "Due date & time must be after start date & time"
    }
    if (selectedTask?.recurring && !selectedTask?.recurrence) {
      newErrors.recurrence = "Select recurrence frequency"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    const payload = {
      title: selectedTask.title.trim(),
      description: selectedTask.description || "",
      statusId: selectedTask.statusId,
      priorityId: selectedTask.priorityId,
      departmentId: selectedTask.departmentId,
      headId: selectedTask.headId,
      assignees: selectedTask.assignees || [],
      startDate: selectedTask.startDate,
      startTime: selectedTask.startTime,
      dueDate: selectedTask.dueDate,
      dueTime: selectedTask.dueTime,
      recurring: !!selectedTask.recurring,
      recurrence: selectedTask.recurring ? selectedTask.recurrence : null,
      isStatutoryTask: !!selectedTask.isStatutoryTask,
    }
    const isEdit = !!selectedTask?.id

    const APIdata = isEdit
      ? await FetchPutData(setLoading, "tasks", selectedTask.id, payload)
      : await FetchPostData(setLoading, "tasks", payload)

    if (APIdata?.data?.success) {
      Swal.fire({
        title: `Task ${isEdit ? "Updated" : "Added"} Successfully.`,
        text: APIdata?.data?.message,
        icon: "success",
      })
      setModalOpen(false)
      setSelectedTask(EMPTY_FORM)
      setCurrentPage(1)
      getTasks()
    } else {
      Swal.fire({
        title: `Error ${isEdit ? "Updating" : "Adding"} Task.`,
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleDelete = item => {
    Swal.fire({
      title: `Are you sure you want to delete "${item?.title}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(setLoading, "tasks", item.id)
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Task Deleted Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          setTaskLogs(prev => prev.filter(l => l.taskId !== item.id))
          getTasks()
        } else {
          Swal.fire({
            title: "Error Deleting Task.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }
  const openAddLogModal = task => {
    setLogForm({
      id: null,
      taskId: task.id,
      dateTime: new Date().toISOString(),
      statusId: task.statusId,
      description: "",
      submittedById: task.assignees?.includes(currentStaffId)
        ? currentStaffId
        : null,
      attachment: null,
      dependancy: false,
      dependancyStaff: [],
    })
    setLogErrors({})
    setLogModalOpen(true)
  }

  const openEditLogModal = log => {
    setLogForm({
      ...log,
      dependancy: !!log.dependancy,
      dependancyStaff: log.dependancyStaffId ? [log.dependancyStaffId] : [],
    })
    setLogErrors({})
    setLogModalOpen(true)
  }

  const formatFileSize = bytes => {
    if (!bytes && bytes !== 0) return ""
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleLogFileChange = e => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      setLogErrors(prev => ({
        ...prev,
        attachment: "File must be under 10MB",
      }))
      return
    }
    setLogErrors(prev => ({ ...prev, attachment: null }))
    setLogForm(prev => ({
      ...prev,
      attachment: { name: file.name, size: file.size, file },
    }))
  }

  const handleAttachmentDownload = attachment => {
    if (attachment?.file) {
      const url = URL.createObjectURL(attachment.file)
      const link = document.createElement("a")
      link.href = url
      link.download = attachment.name
      link.click()
      URL.revokeObjectURL(url)
    } else if (attachment?.path) {
      window.open(`${Configs.imageUrl}${attachment.path}`, "_blank")
    }
  }

  const handleLogSubmit = async () => {
    const newErrors = {}
    if (!logForm.statusId) {
      newErrors.status = "Status is required"
    }
    if (!logForm.submittedById) {
      newErrors.submittedById = "Select who is submitting this log"
    }
    if (!logForm.description?.trim()) {
      newErrors.description = "Log description is required"
    }
    if (logForm.dependancy && !logForm.dependancyStaff?.length) {
      newErrors.dependancyStaff = "Select the staff member this log depends on"
    }
    setLogErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    const payload = {
      statusId: logForm.statusId,
      submittedById: logForm.submittedById,
      description: logForm.description.trim(),
      dependancy: !!logForm.dependancy,
    }
    if (logForm.dependancy) {
      payload.dependancyStaffId = logForm.dependancyStaff[0]
    }
    if (logForm.attachment?.file) {
      payload.attachment = logForm.attachment.file
    } else if (logForm.id && !logForm.attachment) {
      // Attachment was removed while editing
      payload.removeAttachment = true
    }
    const isEdit = !!logForm.id

    const APIdata = isEdit
      ? await FetchPutData(setLoading, "tasks/logs", logForm.id, payload)
      : await FetchPostData(setLoading, `tasks/${logForm.taskId}/logs`, payload)

    if (APIdata?.data?.success) {
      Swal.fire({
        title: `Task Log ${isEdit ? "Updated" : "Added"} Successfully.`,
        text: APIdata?.data?.message,
        icon: "success",
      })
      setLogModalOpen(false)
      loadTaskLogs(logForm.taskId)
      // The task takes the status of its latest log
      getTasks()
    } else {
      Swal.fire({
        title: `Error ${isEdit ? "Updating" : "Adding"} Task Log.`,
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleDeleteLog = log => {
    Swal.fire({
      title: "Delete this log?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(setLoading, "tasks/logs", log.id)
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Task Log Deleted Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          loadTaskLogs(log.taskId)
          getTasks()
        } else {
          Swal.fire({
            title: "Error Deleting Task Log.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const taskLogsFor = taskId =>
    taskLogs
      .filter(log => log.taskId === taskId)
      .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))

  const getReviewStatus = log => log?.reviewStatus || "pending"

  // Only the task head can review logs
  const canReviewLogs =
    !!currentStaffId &&
    data.find(t => t.id === historyTaskId)?.headId === currentStaffId

  const handleApproveLog = async log => {
    const APIdata = await FetchPutData(
      setLoading,
      "tasks/logs",
      `${log.id}/approve`,
      {}
    )
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Log Approved Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
      loadTaskLogs(log.taskId)
    } else {
      Swal.fire({
        title: "Error Approving Log.",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const openRejectModal = log => {
    setRejectTarget(log)
    setRejectReason("")
    setRejectError("")
  }

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      setRejectError("Rejection reason is required")
      return
    }
    const APIdata = await FetchPutData(
      setLoading,
      "tasks/logs",
      `${rejectTarget.id}/reject`,
      { reason: rejectReason.trim() }
    )
    if (APIdata?.data?.success) {
      const taskId = rejectTarget.taskId
      setRejectTarget(null)
      setRejectReason("")
      Swal.fire({
        title: "Log Rejected.",
        text: "The reason has been recorded on this log.",
        icon: "info",
        timer: 1800,
        showConfirmButton: false,
      })
      loadTaskLogs(taskId)
    } else {
      Swal.fire({
        title: "Error Rejecting Log.",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }
  const logTask = data.find(t => t.id === logForm.taskId)
  const nextOccurrence =
    selectedTask?.recurring && selectedTask?.recurrence
      ? getNextOccurrenceText(selectedTask.startDate, selectedTask.recurrence)
      : null
  const historyTask = data.find(t => t.id === historyTaskId)
  const historyLogs = historyTaskId ? taskLogsFor(historyTaskId) : []
  const timelineTask = data.find(t => t.id === timelineTaskId)
  const timelineLogs = timelineTaskId ? taskLogsFor(timelineTaskId) : []

  // Task head currently matches the selected department's head (auto-filled)
  const isAutoDeptHead =
    !!selectedTask?.departmentId &&
    !!selectedTask?.headId &&
    selectedTask.headId === getDepartmentHeadId(selectedTask.departmentId)

  const isOverdue = item =>
    !isClosedStatus(item.statusId) &&
    item.dueDate &&
    moment(item.dueDate).isBefore(moment(), "day")

  const renderTaskHead = headId => {
    if (!headId) return <span className="text-muted">Not assigned</span>
    return (
      <Badge
        pill
        color="warning"
        className="fw-normal text-dark"
        style={{ fontSize: "0.85rem", padding: "0.4em 0.8em" }}
      >
        <FaCrown className="me-1" size={12} />
        {getEmployeeName(headId)}
      </Badge>
    )
  }

  const renderCreatedBy = id => {
    if (!id) return <span className="text-muted">-</span>
    const name = getEmployeeName(id)
    return (
      <div
        className="d-flex align-items-center gap-2"
        style={{ whiteSpace: "nowrap" }}
      >
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
          style={{
            width: 28,
            height: 28,
            fontSize: 11,
            fontWeight: 700,
            backgroundColor: "#cb9019",
            color: "#ffffff",
          }}
          title={name}
        >
          {getInitials(name)}
        </span>
        <span style={{ fontSize: "0.85rem" }}>{name}</span>
      </div>
    )
  }

  const renderLogReview = log => {
    const status = getReviewStatus(log)
    const color =
      status === "approved"
        ? "success"
        : status === "rejected"
        ? "danger"
        : "secondary"
    const Icon =
      status === "approved"
        ? FaCheck
        : status === "rejected"
        ? FaTimes
        : FaClock
    const label = status.charAt(0).toUpperCase() + status.slice(1)
    return (
      <div
        className="d-inline-flex align-items-center gap-2"
        style={{ minWidth: 120 }}
      >
        <Badge
          pill
          color={color}
          className="fw-normal"
          style={{ fontSize: "0.72rem", padding: "0.35em 0.8em" }}
        >
          <Icon className="me-1" size={9} />
          {label}
        </Badge>
        {status === "rejected" && log?.reviewReason && (
          <button
            type="button"
            className="btn btn-link text-decoration-none p-0"
            title="View rejection reason"
            aria-label="View rejection reason"
            onClick={() => setReasonLog(log)}
          >
            <FaEye size={14} color="#dc3545" />
          </button>
        )}
      </div>
    )
  }

  const renderAssignees = ids => {
    if (!ids?.length) return <span className="text-muted">Unassigned</span>
    const visible = ids.slice(0, 2)
    const remaining = ids.length - visible.length
    return (
      <div className="d-flex flex-wrap gap-1">
        {visible.map(id => (
          <Badge
            key={id}
            pill
            color="primary"
            className="fw-normal"
            style={{ fontSize: "0.85rem", padding: "0.4em 0.8em" }}
          >
            {getEmployeeName(id)}
          </Badge>
        ))}
        {remaining > 0 && (
          <Badge
            pill
            color="secondary"
            className="fw-normal"
            style={{ fontSize: "0.85rem", padding: "0.4em 0.8em" }}
          >
            +{remaining} more
          </Badge>
        )}
      </div>
    )
  }

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Dashboard" breadcrumbItem="Task Management" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Toolbar */}
          <div className="bg-light rounded-4 p-3 mb-3">
            <Row className="g-2 align-items-end">
              <Col xs={12} lg>
                <Label className="small text-muted mb-1">Search</Label>
                <InputGroup>
                  <InputGroupText className="bg-white rounded-start-4">
                    <FaSearch className="text-muted" size={14} />
                  </InputGroupText>
                  <Input
                    className="rounded-end-4"
                    placeholder="Title, description, assignee, head or creator..."
                    value={searchString}
                    onChange={e => {
                      setSearchString(e.target.value)
                      resetPage()
                    }}
                  />
                  {searchString && (
                    <Button
                      color="light"
                      className="rounded-4 ms-2 border"
                      title="Clear search"
                      onClick={() => {
                        setSearchString("")
                        resetPage()
                      }}
                    >
                      <FaTimes size={14} />
                    </Button>
                  )}
                </InputGroup>
              </Col>

              <Col xs={6} md={4} lg={2}>
                <Label className="small text-muted mb-1">Priority</Label>
                <Input
                  type="select"
                  value={priorityFilter}
                  onChange={e => {
                    setPriorityFilter(e.target.value)
                    resetPage()
                  }}
                  className="rounded-4"
                >
                  <option value="">All Priority</option>
                  {priorities.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.priorityName}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col xs={6} md={4} lg={1}>
                <Label className="small text-muted mb-1">Show</Label>
                <Input
                  type="select"
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value)), setCurrentPage(1)
                  }}
                  className="rounded-4"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
              </Col>

              <Col xs={12} lg="auto">
                <div className="d-flex gap-2 justify-content-end">
                  <Button
                    color="success"
                    className="rounded-4"
                    onClick={exportToExcel}
                    disabled={filteredData.length === 0}
                    title="Export filtered tasks to Excel"
                  >
                    <FaFileExcel className="me-1" /> Export
                  </Button>
                  {checkAccess(15, "add") && (
                    <Button
                      className="btn-blue rounded-4 px-3"
                      onClick={openAddModal}
                    >
                      <FaPlus className="me-2" />
                      Task
                    </Button>
                  )}
                  {hasActiveFilters && (
                    <Button
                      color="light"
                      className="rounded-4 border"
                      title="Clear all filters"
                      onClick={clearFilters}
                    >
                      <FaTimes size={14} />
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </div>

          {/* Status pills */}
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            {[
              { id: "", name: "", color: "#343a40" },
              ...activeStatuses.map(s => ({
                id: s.id,
                name: s.statusName,
                color: getStatusColor(s.id),
              })),
            ].map(s => {
              const active = statusFilter === s.id
              const label = s.name === "" ? "All" : s.name
              const darkText = active && s.color === "#ffc107"
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setStatusFilter(active ? "" : s.id)
                    resetPage()
                  }}
                  className="rounded-pill px-3 py-1"
                  style={{
                    border: `1px solid ${active ? s.color : "#dee2e6"}`,
                    backgroundColor: active ? s.color : "#fff",
                    color: darkText ? "#212529" : active ? "#fff" : "#495057",
                    fontSize: "0.85rem",
                    fontWeight: active ? 700 : 500,
                  }}
                >
                  {label}
                  <span
                    className="ms-2 rounded-pill px-2"
                    style={{
                      fontSize: "0.75rem",
                      backgroundColor: active
                        ? darkText
                          ? "rgba(0,0,0,.12)"
                          : "rgba(255,255,255,.25)"
                        : "#eef1f6",
                      color: darkText ? "#212529" : active ? "#fff" : "#495057",
                    }}
                  >
                    {statusCounts[s.id] ?? 0}
                  </span>
                </button>
              )
            })}
          </div>

          {/* Table */}
          <Table bordered responsive>
            <thead className="table-dark">
              <tr>
                <th style={{ width: "50px" }}>#</th>
                <th style={{ minWidth: "220px" }}>Task</th>
                <th>Status</th>
                <th>Assignees</th>
                <th style={{ width: "150px" }}>Task Head</th>
                <th style={{ width: "170px" }}>Created By</th>
                <th>Start Date</th>
                <th>Due Date</th>
                <th>Priority</th>
                <th className="text-center" style={{ width: "190px" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center">
                    No tasks found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr
                    key={item.id}
                    onClick={() => openTimeline(item.id)}
                    style={{ cursor: "pointer" }}
                    title="Click to view log timeline"
                    className={
                      getStatusName(item.statusId).toLowerCase() === "completed"
                        ? "table-success"
                        : isOverdue(item)
                        ? "table-danger"
                        : ""
                    }
                  >
                    <td>{start + index + 1}</td>

                    <td>
                      <div className="fw-bold">
                        {item.title}
                        {item.recurring && (
                          <Badge
                            pill
                            color="info"
                            className="ms-2 fw-normal"
                            title={`Repeats ${item.recurrence}`}
                          >
                            <FaSync size={10} className="me-1" />
                            {item.recurrence}
                          </Badge>
                        )}
                      </div>
                      {item.description && (
                        <div
                          className="text-muted small text-truncate"
                          style={{ maxWidth: "280px" }}
                          title={item.description}
                        >
                          {item.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <Badge
                        pill
                        style={{
                          backgroundColor: getStatusColor(item.statusId),
                          fontSize: "0.78rem",
                          padding: "0.4em 0.9em",
                        }}
                      >
                        {getStatusName(item.statusId)}
                      </Badge>
                    </td>
                    <td>{renderAssignees(item.assignees)}</td>
                    <td>{renderTaskHead(item.headId)}</td>
                    <td>{renderCreatedBy(item.createdById)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {formatTaskDateTime(item.startDate, item.startTime)}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {formatTaskDateTime(item.dueDate, item.dueTime)}
                    </td>
                    <td>
                      <Badge
                        pill
                        color={getPriorityColor(item.priorityId)}
                        style={{ fontSize: "0.85rem", padding: "0.4em 0.9em" }}
                      >
                        {getPriority(item.priorityId)?.priorityName || "-"}
                      </Badge>
                    </td>

                    <td
                      className="text-center"
                      style={{ whiteSpace: "nowrap" }}
                      onClick={e => e.stopPropagation()}
                    >
                      <Button
                        className="bg-info text-white me-2 border-0 shadow-none"
                        title="Add Task Log"
                        onClick={() => openAddLogModal(item)}
                      >
                        <MdNoteAdd size={18} />
                      </Button>

                      <Button
                        className="bg-secondary text-white me-2 border-0 shadow-none"
                        title="View Log History"
                        onClick={() => openHistory(item.id)}
                      >
                        <FaHistory size={15} />
                      </Button>

                      {checkAccess(15, "edit") && (
                        <Button
                          className="bg-success text-white me-2 border-0 shadow-none"
                          title="Edit Task"
                          onClick={() => openEditModal(item)}
                        >
                          <FaEdit size={16} />
                        </Button>
                      )}

                      {checkAccess(15, "delete") && (
                        <Button
                          className="bg-danger text-white border-0 shadow-none"
                          title="Delete Task"
                          onClick={() => handleDelete(item)}
                        >
                          <MdDelete size={18} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <nav
            aria-label="Page navigation example"
            className="d-flex justify-content-between mt-3"
          >
            <h5>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        centered
        fullscreen
      >
        <div
          className="d-flex align-items-center gap-3 px-4 py-3 text-white"
          style={{
            background: "linear-gradient(120deg, #8a6414, #cb9019)",
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 52,
              height: 52,
              borderRadius: 12,
              background: "linear-gradient(145deg, #e3b23c, #a57612)",
            }}
          >
            <FaTasks size={26} />
          </div>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <h5 className="mb-0 fw-bold">
              {selectedTask?.id ? "Edit" : "Add"} Task
            </h5>
            <small style={{ color: "#f7ecd4" }}>
              {selectedTask?.id
                ? "Update the task details below."
                : "Create a new task and keep your work on track."}
            </small>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setModalOpen(false)}
            className="btn btn-link text-decoration-none p-0"
            style={{ color: "#e8eef9", fontSize: 28, lineHeight: 1 }}
          >
            <FaTimes size={20} />
          </button>
        </div>
        <ModalBody style={{ padding: "26px 28px 20px" }}>
          <Row>
            <Col xs={12} md={9} className="mb-3">
              <Label className="fw-bold" style={{ fontSize: 14 }}>
                Title <span className="text-danger">*</span>
              </Label>
              <div style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: 15,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#72829c",
                  }}
                >
                  <FaFileAlt size={17} />
                </span>
                <Input
                  placeholder="e.g. Prepare monthly sales report"
                  value={selectedTask?.title || ""}
                  invalid={!!errors.title}
                  onChange={e =>
                    setSelectedTask(prev => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  style={{ ...TASK_INPUT_STYLE, height: 52, paddingLeft: 45 }}
                />
              </div>
              {errors.title && (
                <FormFeedback className="d-block">{errors.title}</FormFeedback>
              )}
            </Col>

            {checkAccess(19, "view") && (
            <Col xs={12} md={3} className="mb-3">
              <Label className="fw-bold" style={{ fontSize: 14 }}>
                Statutory Task
              </Label>
              {(() => {
                const checked = !!selectedTask?.isStatutoryTask
                return (
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={checked}
                    onClick={() =>
                      setSelectedTask(prev => ({
                        ...prev,
                        isStatutoryTask: !prev.isStatutoryTask,
                      }))
                    }
                    className="d-flex align-items-center w-100"
                    style={{
                      height: 52,
                      gap: 10,
                      padding: "0 14px",
                      borderRadius: 11,
                      border: `1px solid ${checked ? "#cb9019" : "#d8e1ef"}`,
                      background: checked
                        ? "linear-gradient(105deg, #fdf9ef, #faf3e0)"
                        : "#fff",
                      color: checked ? "#8a6414" : "#4b607f",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all .15s ease",
                    }}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 7,
                        border: `1.5px solid ${
                          checked ? "#cb9019" : "#b9c5d8"
                        }`,
                        background: checked ? "#cb9019" : "#fff",
                        color: "#fff",
                        flexShrink: 0,
                        transition: "all .15s ease",
                      }}
                    >
                      {checked && <FaCheck size={11} />}
                    </span>
                    <span>{checked ? "Statutory" : "Not statutory"}</span>
                  </button>
                )
              })()}
            </Col>
            )}

            <Col xs={12} md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: getStatusColor(selectedTask?.statusId),
                      boxShadow: `0 0 0 3px ${getStatusColor(
                        selectedTask?.statusId
                      )}2e`,
                    }}
                  />
                  Status <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={selectedTask?.statusId || ""}
                  invalid={!!errors.status}
                  onChange={e =>
                    setSelectedTask(prev => ({
                      ...prev,
                      statusId: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                  style={{
                    ...TASK_INPUT_STYLE,
                    borderColor: "#ffe0a1",
                    backgroundColor: "#fffaf0",
                  }}
                >
                  <option value="">Select status</option>
                  {statusOptionsFor(selectedTask?.statusId).map(s => (
                    <option key={s.id} value={s.id}>
                      {s.statusName}
                    </option>
                  ))}
                </Input>
                {errors.status && (
                  <FormFeedback className="d-block">
                    {errors.status}
                  </FormFeedback>
                )}
              </div>
            </Col>

            <Col xs={12} md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <FaFlag
                    size={TASK_LABEL_ICON_STYLE.size}
                    color={TASK_LABEL_ICON_STYLE.color}
                  />
                  Priority
                </Label>
                <Input
                  type="select"
                  value={selectedTask?.priorityId || ""}
                  onChange={handlePriorityChange}
                  style={{
                    ...TASK_INPUT_STYLE,
                    borderColor: "#ecd9a8",
                    backgroundColor: "#fdf8ec",
                    fontWeight: 600,
                  }}
                >
                  {priorities.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.priorityName} ({p.value} {getPriorityUnit(p)})
                    </option>
                  ))}
                  {priorities.length === 0 && (
                    <option value="" disabled>
                      No priorities defined
                    </option>
                  )}
                </Input>
              </div>
            </Col>

            <Col xs={12} md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <FaBuilding
                    size={TASK_LABEL_ICON_STYLE.size}
                    color={TASK_LABEL_ICON_STYLE.color}
                  />
                  Department
                </Label>
                <Input
                  type="select"
                  value={selectedTask?.departmentId || ""}
                  onChange={handleDepartmentChange}
                  style={TASK_INPUT_STYLE}
                >
                  <option value="">Select department</option>
                  {departments
                    .filter(
                      dept =>
                        dept.isActive || dept.id === selectedTask?.departmentId
                    )
                    .map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.departmentName}
                      </option>
                    ))}
                </Input>
              </div>
            </Col>

            <Col xs={12} md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <FaCrown
                    size={TASK_LABEL_ICON_STYLE.size}
                    color={TASK_LABEL_ICON_STYLE.color}
                  />
                  Task Head <span className="text-danger">*</span>
                </Label>
                <Select
                  options={employeeOptions}
                  placeholder="Select task head"
                  styles={ASSIGNEE_SELECT_STYLES}
                  value={
                    employeeOptions.find(
                      opt => opt.value === selectedTask?.headId
                    ) || null
                  }
                  onChange={selected =>
                    setSelectedTask(prev => ({
                      ...prev,
                      headId: selected?.value ?? null,
                    }))
                  }
                />
                {errors.headId ? (
                  <div className="text-danger small mt-1">{errors.headId}</div>
                ) : (
                  <small className="text-muted d-block mt-1">
                    {isAutoDeptHead
                      ? "Auto-filled with the department head — you can change it."
                      : "Mandatory. Pick any staff member."}
                  </small>
                )}
              </div>
            </Col>

            <Col xs={12} sm={6} md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <FaCalendarAlt
                    size={TASK_LABEL_ICON_STYLE.size}
                    color={TASK_LABEL_ICON_STYLE.color}
                  />
                  Start Date <span className="text-danger">*</span>
                </Label>
                <Input
                  type="date"
                  value={selectedTask?.startDate || ""}
                  invalid={!!errors.startDate}
                  onChange={e =>
                    setSelectedTask(prev => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  style={TASK_INPUT_STYLE}
                />
                {errors.startDate && (
                  <FormFeedback className="d-block">
                    {errors.startDate}
                  </FormFeedback>
                )}
              </div>
            </Col>

            <Col xs={12} sm={6} md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <FaClock
                    size={TASK_LABEL_ICON_STYLE.size}
                    color={TASK_LABEL_ICON_STYLE.color}
                  />
                  Start Time <span className="text-danger">*</span>
                </Label>
                <Input
                  type="time"
                  value={selectedTask?.startTime || ""}
                  invalid={!!errors.startTime}
                  onChange={e =>
                    setSelectedTask(prev => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                  style={TASK_INPUT_STYLE}
                />
                {errors.startTime && (
                  <FormFeedback className="d-block">
                    {errors.startTime}
                  </FormFeedback>
                )}
              </div>
            </Col>

            <Col xs={12} sm={6} md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <FaCalendarAlt
                    size={TASK_LABEL_ICON_STYLE.size}
                    color={TASK_LABEL_ICON_STYLE.color}
                  />
                  Due Date <span className="text-danger">*</span>
                </Label>
                <Input
                  type="date"
                  min={selectedTask?.startDate || ""}
                  value={selectedTask?.dueDate || ""}
                  invalid={!!errors.dueDate}
                  onChange={e =>
                    setSelectedTask(prev => ({
                      ...prev,
                      dueDate: e.target.value,
                    }))
                  }
                  style={TASK_INPUT_STYLE}
                />
                {errors.dueDate && (
                  <FormFeedback className="d-block">
                    {errors.dueDate}
                  </FormFeedback>
                )}
              </div>
            </Col>

            <Col xs={12} sm={6} md={3} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <FaClock
                    size={TASK_LABEL_ICON_STYLE.size}
                    color={TASK_LABEL_ICON_STYLE.color}
                  />
                  Due Time <span className="text-danger">*</span>
                </Label>
                <Input
                  type="time"
                  value={selectedTask?.dueTime || ""}
                  invalid={!!errors.dueTime}
                  onChange={e =>
                    setSelectedTask(prev => ({
                      ...prev,
                      dueTime: e.target.value,
                    }))
                  }
                  style={TASK_INPUT_STYLE}
                />
                {errors.dueTime && (
                  <FormFeedback className="d-block">
                    {errors.dueTime}
                  </FormFeedback>
                )}
              </div>
            </Col>

            <Col xs={12} className="mb-3">
              <div style={TASK_FIELD_CARD_STYLE}>
                <Label
                  className="fw-bold d-flex align-items-center gap-2"
                  style={{ fontSize: 14 }}
                >
                  <FaUsers
                    size={TASK_LABEL_ICON_STYLE.size}
                    color={TASK_LABEL_ICON_STYLE.color}
                  />
                  Assignees
                </Label>
                <Select
                  isMulti
                  options={employeeOptions}
                  placeholder="Select assignees"
                  styles={ASSIGNEE_SELECT_STYLES}
                  value={employeeOptions.filter(opt =>
                    selectedTask?.assignees?.includes(opt.value)
                  )}
                  onChange={selected =>
                    setSelectedTask(prev => ({
                      ...prev,
                      assignees: (selected || []).map(opt => opt.value),
                    }))
                  }
                />
              </div>
            </Col>

            {checkAccess(19, "view") && (
            <Col md={12} className="mb-3">
              <div
                style={{
                  padding: "16px 18px",
                  border: "1px solid #efe0bd",
                  borderRadius: 15,
                  background: "linear-gradient(105deg, #fdf9ef, #faf3e0)",
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div
                    className="d-flex align-items-center gap-2 fw-bold"
                    style={{ fontSize: 14 }}
                  >
                    <FaSync size={18} color="#ad7e1f" />
                    Recurring
                  </div>
                  <Switch
                    checked={selectedTask?.recurring ?? false}
                    onChange={checked =>
                      setSelectedTask(prev => ({
                        ...prev,
                        recurring: checked,
                        recurrence: checked ? prev.recurrence : null,
                      }))
                    }
                  />
                </div>
                {selectedTask?.recurring && (
                  <div className="d-flex align-items-center flex-wrap mt-3">
                    {RECURRENCE_OPTIONS.map(opt => {
                      const active = selectedTask?.recurrence === opt
                      return (
                        <button
                          key={opt}
                          type="button"
                          aria-pressed={active}
                          onClick={() =>
                            setSelectedTask(prev => ({
                              ...prev,
                              recurrence: opt,
                            }))
                          }
                          className="d-inline-flex align-items-center me-2 mb-2"
                          style={{
                            gap: 9,
                            minHeight: 40,
                            padding: "0 15px",
                            border: active
                              ? "1px solid #cb9019"
                              : "1px solid #e3d3a8",
                            borderRadius: 999,
                            color: active ? "#fff" : "#6b5a33",
                            background: active
                              ? "linear-gradient(135deg, #d9a62e, #ad7e1f)"
                              : "#fff",
                            fontSize: 14,
                            fontWeight: 650,
                            boxShadow: active
                              ? "0 4px 10px rgba(203,144,25,.25)"
                              : "none",
                          }}
                        >
                          <span
                            style={{
                              width: 17,
                              height: 17,
                              borderRadius: "50%",
                              border: active
                                ? "2px solid #fff"
                                : "2px solid #d3c096",
                              background: active ? "#f3e3bb" : "#fff",
                              boxShadow: active
                                ? "inset 0 0 0 4px #fff"
                                : "none",
                            }}
                          />
                          {opt}
                        </button>
                      )
                    })}
                    <span
                      className="d-inline-flex align-items-center mb-2 ms-2"
                      style={{ gap: 8, color: "#7b8ba5", fontSize: 12 }}
                    >
                      <FaCalendarAlt size={15} color="#ad7e1f" />
                      {nextOccurrence
                        ? `Next on ${nextOccurrence}`
                        : "Select a start date to calculate the next occurrence."}
                    </span>
                  </div>
                )}
                {errors.recurrence && (
                  <div className="text-danger small mt-1">
                    {errors.recurrence}
                  </div>
                )}
              </div>
            </Col>
            )}

            <Col md={12}>
              <Label
                className="fw-bold d-flex align-items-center gap-2"
                style={{ fontSize: 14 }}
              >
                <FaAlignLeft
                  size={TASK_LABEL_ICON_STYLE.size}
                  color={TASK_LABEL_ICON_STYLE.color}
                />
                Description
              </Label>
              <Input
                type="textarea"
                rows={2}
                placeholder="Task details (optional)"
                value={selectedTask?.description || ""}
                onChange={e =>
                  setSelectedTask(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                style={{
                  minHeight: 76,
                  padding: "13px 15px",
                  lineHeight: 1.5,
                  fontSize: 14,
                  borderRadius: 11,
                  borderColor: "#d8e1ef",
                }}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter
          className="d-flex justify-content-end gap-2"
          style={{
            padding: "16px 28px 22px",
            borderTop: "1px solid #edf1f7",
          }}
        >
          <Button
            className="btn-danger"
            onClick={() => setModalOpen(false)}
            style={{
              minWidth: 112,
              height: 46,
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            <MdCancel className="me-2" /> Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            style={{
              minWidth: 112,
              height: 46,
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
              color: "#fff",
              borderColor: "#cb9019",
              backgroundColor: "#cb9019",
              borderWidth: 1,
            }}
          >
            <FaSave className="me-2" /> Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* Themed checkbox for the log dependancy control */}
      <style>{`
        .form-check-input.dependancy-check {
          background-color: #ffffff;
          border-color: #cb9019;
        }
        .form-check-input.dependancy-check:checked {
          background-color: #cb9019;
          border-color: #cb9019;
        }
        .form-check-input.dependancy-check:focus {
          border-color: #cb9019;
          box-shadow: 0 0 0 0.2rem rgba(203, 144, 25, 0.35);
        }
      `}</style>

      {/* Add / Edit Task Log Modal */}
      <Modal
        isOpen={logModalOpen}
        toggle={() => setLogModalOpen(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setLogModalOpen(false)}>
          {logForm.id ? "Edit" : "Add"} Task Log
          {logTask && (
            <div className="small text-muted fw-normal">{logTask.title}</div>
          )}
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Label className="mb-1" style={LOG_LABEL_STYLE}>
                Date & Time
              </Label>
              <Input
                disabled
                style={LOG_INPUT_STYLE}
                value={
                  logForm.dateTime
                    ? moment(logForm.dateTime).format("DD-MM-YYYY | hh:mm A")
                    : ""
                }
              />
            </Col>

            <Col xs={12} md={6}>
              <Label className="mb-1" style={LOG_LABEL_STYLE}>
                Submitted By <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                style={LOG_INPUT_STYLE}
                value={logForm.submittedById || ""}
                invalid={!!logErrors.submittedById}
                onChange={e =>
                  setLogForm(prev => ({
                    ...prev,
                    submittedById: e.target.value
                      ? Number(e.target.value)
                      : null,
                  }))
                }
              >
                <option value="">Select employee</option>
                {(logTask?.assignees?.length
                  ? logTask.assignees
                  : employees.map(emp => emp.id)
                ).map(id => (
                  <option key={id} value={id}>
                    {getEmployeeName(id)}
                  </option>
                ))}
              </Input>
              {logErrors.submittedById && (
                <FormFeedback className="d-block">
                  {logErrors.submittedById}
                </FormFeedback>
              )}
            </Col>

            <Col xs={12} md={6}>
              <Label className="mb-1" style={LOG_LABEL_STYLE}>
                Status <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                style={LOG_INPUT_STYLE}
                value={logForm.statusId || ""}
                invalid={!!logErrors.status}
                onChange={e =>
                  setLogForm(prev => ({
                    ...prev,
                    statusId: e.target.value ? Number(e.target.value) : null,
                  }))
                }
              >
                {statusOptionsFor(logForm.statusId).map(s => (
                  <option key={s.id} value={s.id}>
                    {s.statusName}
                  </option>
                ))}
              </Input>
              {logErrors.status && (
                <FormFeedback className="d-block">
                  {logErrors.status}
                </FormFeedback>
              )}
            </Col>

            <Col xs={12} md={6}>
              <Label className="mb-1" style={LOG_LABEL_STYLE}>
                Attachment
              </Label>
              <Input
                type="file"
                onChange={handleLogFileChange}
                style={{
                  height: 48,
                  fontSize: 14,
                  padding: "11px 12px",
                  borderRadius: 11,
                  borderColor: "#d8e1ef",
                }}
                className={
                  logErrors.attachment ? "is-invalid rounded-4" : "rounded-4"
                }
              />
              {logErrors.attachment && (
                <div className="invalid-feedback d-block">
                  {logErrors.attachment}
                </div>
              )}
              {logForm.attachment && (
                <div className="d-flex align-items-center gap-2 mt-2 border rounded-4 px-3 py-2">
                  <FaPaperclip size={13} className="text-muted" />
                  <span className="small flex-grow-1 text-truncate">
                    {logForm.attachment.name}
                    {logForm.attachment.size &&
                      ` (${formatFileSize(logForm.attachment.size)})`}
                  </span>
                  <Button
                    size="sm"
                    color="link"
                    className="text-danger p-0 text-decoration-none"
                    title="Remove attachment"
                    onClick={() =>
                      setLogForm(prev => ({ ...prev, attachment: null }))
                    }
                  >
                    <FaTimes size={13} />
                  </Button>
                </div>
              )}
            </Col>

            <Col xs={12}>
              <div
                style={{
                  ...LOG_FIELD_CARD_STYLE,
                  ...(logForm.dependancy
                    ? {
                        borderColor: "#e2bd63",
                        background: "linear-gradient(145deg, #fffdf8, #fcf4e4)",
                      }
                    : {}),
                }}
              >
                <Row className="g-3 align-items-center">
                  <Col xs={12} md={3}>
                    <div className="d-flex align-items-center gap-2">
                      <Input
                        type="checkbox"
                        id="logDependancy"
                        className="dependancy-check"
                        checked={!!logForm.dependancy}
                        onChange={e => {
                          const checked = e.target.checked
                          setLogForm(prev => ({
                            ...prev,
                            dependancy: checked,
                            // Clear the selection when the checkbox is unticked
                            dependancyStaff: checked
                              ? prev.dependancyStaff || []
                              : [],
                          }))
                          if (!checked) {
                            setLogErrors(prev => ({
                              ...prev,
                              dependancyStaff: null,
                            }))
                          }
                        }}
                        style={{
                          width: 20,
                          height: 20,
                          marginTop: 0,
                          cursor: "pointer",
                        }}
                      />
                      <Label
                        htmlFor="logDependancy"
                        className="mb-0"
                        style={{
                          ...LOG_LABEL_STYLE,
                          color: logForm.dependancy ? "#8a6414" : "#263a5b",
                        }}
                      >
                        Dependancy
                      </Label>
                    </div>
                  </Col>

                  {logForm.dependancy ? (
                    <Col xs={12} md={9}>
                      <Select
                        aria-label="Dependent staff"
                        options={employeeOptions}
                        placeholder="Select a staff member"
                        styles={ASSIGNEE_SELECT_STYLES}
                        value={
                          employeeOptions.find(opt =>
                            logForm.dependancyStaff?.includes(opt.value)
                          ) || null
                        }
                        onChange={selected => {
                          // Single staff only — keep it in the array field
                          const dependancyStaff = selected
                            ? [selected.value]
                            : []
                          setLogForm(prev => ({ ...prev, dependancyStaff }))
                          if (dependancyStaff.length) {
                            setLogErrors(prev => ({
                              ...prev,
                              dependancyStaff: null,
                            }))
                          }
                        }}
                      />
                      {logErrors.dependancyStaff && (
                        <div className="invalid-feedback d-block">
                          {logErrors.dependancyStaff}
                        </div>
                      )}
                    </Col>
                  ) : (
                    <Col xs={12} md={9}>
                      <small className="text-muted">
                        Tick the box to choose the staff this update depends on.
                      </small>
                    </Col>
                  )}
                </Row>
              </div>
            </Col>

            <Col xs={12}>
              <Label className="mb-1" style={LOG_LABEL_STYLE}>
                Log Description <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                rows={3}
                placeholder="What progress was made?"
                style={LOG_TEXTAREA_STYLE}
                value={logForm.description}
                invalid={!!logErrors.description}
                onChange={e =>
                  setLogForm(prev => ({ ...prev, description: e.target.value }))
                }
              />
              {logErrors.description && (
                <FormFeedback className="d-block">
                  {logErrors.description}
                </FormFeedback>
              )}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter
          className="d-flex justify-content-end gap-2"
          style={{ borderTop: "1px solid #edf1f7" }}
        >
          <Button className="btn-blue" onClick={handleLogSubmit}>
            <FaSave /> Save
          </Button>
          <Button className="btn-danger" onClick={() => setLogModalOpen(false)}>
            <MdCancel /> Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Task Log History Modal */}
      <Modal
        isOpen={!!historyTaskId}
        toggle={() => setHistoryTaskId(null)}
        centered
        fullscreen
      >
        <ModalHeader toggle={() => setHistoryTaskId(null)}>
          Task Log History
          {historyTask && (
            <div className="small text-muted fw-normal">
              {historyTask.title}
            </div>
          )}
          {historyTask && !canReviewLogs && (
            <div className="small fw-normal mt-1" style={{ color: "#8a6414" }}>
              Only the task head can approve or reject these logs —{" "}
              {getEmployeeName(historyTask.headId)}.
            </div>
          )}
        </ModalHeader>
        <ModalBody style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {historyLogs.length === 0 ? (
            <p className="text-center text-muted mb-0">
              No logs added for this task yet.
            </p>
          ) : (
            <Table bordered responsive className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "50px" }}>#</th>
                  <th style={{ whiteSpace: "nowrap" }}>Date & Time</th>
                  <th>Submitted By</th>
                  <th>Status</th>
                  <th style={{ minWidth: "200px" }}>Description</th>
                  <th>Dependency On</th>
                  <th>Attachment</th>
                  <th style={{ minWidth: "150px" }}>Review</th>
                  <th
                    className="text-center"
                    style={{ width: canReviewLogs ? 240 : 110 }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {historyLogs.map((log, index) => (
                  <tr key={log.id}>
                    <td>{index + 1}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {moment(log.dateTime).format("DD-MM-YYYY | hh:mm A")}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {getEmployeeName(log.submittedById)}
                    </td>
                    <td>
                      <Badge
                        pill
                        style={{
                          backgroundColor: getStatusColor(log.statusId),
                          fontSize: "0.75rem",
                          padding: "0.35em 0.8em",
                        }}
                      >
                        {getStatusName(log.statusId)}
                      </Badge>
                    </td>
                    <td>{log.description}</td>
                    <td>{log?.dependancyStaff?.name || "-"}</td>
                    <td>
                      {log.attachment ? (
                        <button
                          type="button"
                          className={`btn btn-link btn-sm p-0 text-decoration-none ${
                            log.attachment.file || log.attachment.path
                              ? ""
                              : "text-muted"
                          }`}
                          title={
                            log.attachment.file || log.attachment.path
                              ? "Download attachment"
                              : log.attachment.name
                          }
                          onClick={() =>
                            handleAttachmentDownload(log.attachment)
                          }
                          style={{
                            fontSize: "0.8rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <FaPaperclip size={12} className="me-1" />
                          {log.attachment.name}
                          {log.attachment.size &&
                            ` (${formatFileSize(log.attachment.size)})`}
                        </button>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{renderLogReview(log)}</td>
                    <td
                      className="text-center"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {canReviewLogs && (
                        <>
                          {/* A rejected log is final — no approve option */}
                          {getReviewStatus(log) !== "rejected" && (
                            <Button
                              size="sm"
                              className="bg-success text-white me-1 border-0 shadow-none"
                              title="Approve log"
                              disabled={getReviewStatus(log) === "approved"}
                              onClick={() => handleApproveLog(log)}
                            >
                              <FaCheck size={12} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="bg-danger text-white me-2 border-0 shadow-none"
                            title="Reject log"
                            disabled={getReviewStatus(log) === "rejected"}
                            onClick={() => openRejectModal(log)}
                          >
                            <FaTimes size={12} />
                          </Button>
                        </>
                      )}
                      {/* Reviewed logs are locked — edit / delete only while pending */}
                      {getReviewStatus(log) === "pending" ? (
                        <>
                          <Button
                            size="sm"
                            className="bg-secondary text-white me-2 border-0 shadow-none"
                            title="Edit log"
                            onClick={() => openEditLogModal(log)}
                          >
                            <FaEdit size={12} />
                          </Button>
                          <Button
                            size="sm"
                            className="bg-danger text-white border-0 shadow-none"
                            title="Delete log"
                            onClick={() => handleDeleteLog(log)}
                          >
                            <MdDelete size={14} />
                          </Button>
                        </>
                      ) : (
                        !canReviewLogs && <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
      </Modal>

      {/* Reject Log — reason modal (task head only) */}
      <Modal
        isOpen={!!rejectTarget}
        toggle={() => setRejectTarget(null)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setRejectTarget(null)}>
          Reject Log
        </ModalHeader>
        <ModalBody>
          <p className="mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
            {rejectTarget?.description}
          </p>
          <div className="small text-muted mb-3">
            Submitted by {getEmployeeName(rejectTarget?.submittedById)} on{" "}
            {rejectTarget?.dateTime
              ? moment(rejectTarget.dateTime).format("DD-MM-YYYY")
              : "-"}
          </div>

          <Label className="mb-1" style={{ fontSize: 14, fontWeight: 700 }}>
            Reason for rejection <span className="text-danger">*</span>
          </Label>
          <Input
            type="textarea"
            rows={3}
            autoFocus
            value={rejectReason}
            invalid={!!rejectError}
            placeholder="Why is this log being rejected?"
            onChange={e => {
              setRejectReason(e.target.value)
              setRejectError("")
            }}
            style={{
              fontSize: 14,
              borderRadius: 11,
              borderColor: "#d8e1ef",
            }}
          />
          {rejectError && (
            <FormFeedback className="d-block">{rejectError}</FormFeedback>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end gap-2">
          <Button className="btn-danger" onClick={handleRejectSubmit}>
            <FaTimes className="me-1" size={12} /> Reject
          </Button>
          <Button color="secondary" onClick={() => setRejectTarget(null)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* View Rejection Reason Modal */}
      <Modal
        isOpen={!!reasonLog}
        toggle={() => setReasonLog(null)}
        centered
        size="md"
      >
        <ModalHeader
          toggle={() => setReasonLog(null)}
          style={{ fontSize: 17, fontWeight: 700 }}
        >
          Rejection Reason
        </ModalHeader>
        <ModalBody style={{ padding: "24px 26px" }}>
          <p
            className="mb-1"
            style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.5 }}
          >
            {reasonLog?.description}
          </p>
          <div className="text-muted mb-4" style={{ fontSize: 14 }}>
            Submitted by {getEmployeeName(reasonLog?.submittedById)} on{" "}
            {reasonLog?.dateTime
              ? moment(reasonLog.dateTime).format("DD-MM-YYYY")
              : "-"}
          </div>

          <div
            className="rounded-4 p-4"
            style={{ background: "#fdf3f3", border: "1px solid #f5c2c7" }}
          >
            <div
              className="text-danger mb-2"
              style={{ fontSize: 13, fontWeight: 700 }}
            >
              Reason
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.7 }}>
              {reasonLog?.reviewReason}
            </div>
          </div>

          <div className="text-muted mt-4 mb-0" style={{ fontSize: 14 }}>
            Rejected by {getEmployeeName(reasonLog?.reviewedById)} on{" "}
            {reasonLog?.reviewedAt
              ? moment(reasonLog.reviewedAt).format("DD-MM-YYYY | hh:mm A")
              : "-"}
          </div>
        </ModalBody>
      </Modal>

      {/* Log Timeline (read-only) Modal */}
      <Modal
        isOpen={!!timelineTaskId}
        toggle={() => setTimelineTaskId(null)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setTimelineTaskId(null)}>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span>Log Timeline</span>
            {timelineTask && (
              <Badge
                pill
                style={{
                  backgroundColor: getStatusColor(timelineTask.statusId),
                  fontSize: "0.72rem",
                  padding: "0.35em 0.8em",
                }}
              >
                {getStatusName(timelineTask.statusId)}
              </Badge>
            )}
          </div>
          {timelineTask && (
            <div className="small text-muted fw-normal">
              {timelineTask.title} · {timelineLogs.length}{" "}
              {timelineLogs.length === 1 ? "update" : "updates"}
            </div>
          )}
        </ModalHeader>
        <ModalBody
          className="bg-light"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          {timelineLogs.length === 0 ? (
            <div className="text-center py-5">
              <FaClipboardList size={44} className="text-muted mb-3" />
              <h6 className="fw-bold">No updates yet</h6>
              <p className="text-muted small mb-0">
                Logs added by assignees will appear here.
              </p>
            </div>
          ) : (
            timelineLogs.map((log, index) => {
              const color = getStatusColor(log.statusId)
              const isLast = index === timelineLogs.length - 1
              return (
                <div key={log.id} className={`d-flex ${isLast ? "" : "pb-1"}`}>
                  <div className="d-flex flex-column align-items-center me-3">
                    <div
                      className="rounded-circle flex-shrink-0 mt-1"
                      style={{
                        width: "15px",
                        height: "15px",
                        backgroundColor: color,
                        boxShadow: `0 0 0 4px ${color}26`,
                      }}
                    />
                    {!isLast && (
                      <div
                        className="flex-grow-1"
                        style={{
                          width: "2px",
                          minHeight: "28px",
                          backgroundColor: "#dee2e6",
                        }}
                      />
                    )}
                  </div>
                  <div
                    className={`flex-grow-1 bg-white border rounded-3 p-3 ${
                      isLast ? "" : "mb-3"
                    }`}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-2">
                      <Badge
                        pill
                        style={{
                          backgroundColor: color,
                          fontSize: "0.75rem",
                          padding: "0.35em 0.85em",
                        }}
                      >
                        {getStatusName(log.statusId)}
                      </Badge>
                      <small
                        className="text-muted"
                        title={moment(log.dateTime).format(
                          "DD-MM-YYYY | hh:mm A"
                        )}
                      >
                        {moment(log.dateTime).fromNow()}
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span
                        className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                        style={{
                          width: "32px",
                          height: "32px",
                          fontSize: "0.75rem",
                          backgroundColor: color,
                          color: color === "#ffc107" ? "#212529" : "#fff",
                        }}
                      >
                        {getInitials(getEmployeeName(log.submittedById))}
                      </span>
                      <div>
                        <div className="fw-bold" style={{ fontSize: "0.9rem" }}>
                          {getEmployeeName(log.submittedById)}
                        </div>
                        <small className="text-muted">
                          {moment(log.dateTime).format("DD-MM-YYYY | hh:mm A")}
                        </small>
                      </div>
                    </div>
                    <p className="mb-0">{log.description}</p>
                    {log.attachment && (
                      <button
                        type="button"
                        className="btn btn-light btn-sm mt-2 border rounded-pill px-3 text-decoration-none"
                        title={
                          log.attachment.file || log.attachment.path
                            ? "Download attachment"
                            : log.attachment.name
                        }
                        onClick={() => handleAttachmentDownload(log.attachment)}
                        style={{ fontSize: "0.8rem" }}
                      >
                        <FaPaperclip size={12} className="me-1" />
                        {log.attachment.name}
                        {log.attachment.size &&
                          ` (${formatFileSize(log.attachment.size)})`}
                      </button>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default TaskManagement
